import { disablePostHog, getPostHogStorageKeys } from '../hooks/usePostHog';

const CONSENT_VERSION = '2.0';

/**
 * Migrate consent data from v1.0 to v2.0 structure
 * v1.0: { necessary, analytics, timestamp, version: '1.0' }
 * v2.0: { necessary, analytics, notifications, timestamp, version: '2.0' }
 * @param {Object} consentData - Existing consent data
 * @returns {Object} Migrated consent data
 */
const migrateConsentData = (consentData) => {
  if (!consentData || consentData.version === CONSENT_VERSION) {
    return consentData;
  }

  if (consentData.version === '1.0') {
    const migratedData = {
      necessary: true,
      analytics: consentData.analytics === true,
      notifications: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    
    localStorage.setItem('cookie_consent', JSON.stringify(migratedData));
    return migratedData;
  }

  return consentData;
};

/**
 * Get consent data with automatic migration
 * @returns {Object|null} Consent data or null
 */
export const getConsentData = () => {
  try {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      return null;
    }
    
    const consentData = JSON.parse(consent);
    return migrateConsentData(consentData);
  } catch (error) {
    return null;
  }
};

/**
 * Check if user has made a consent decision
 * @returns {boolean} True if consent decision exists
 */
export const hasConsentDecision = () => {
  try {
    const consent = localStorage.getItem('cookie_consent');
    return consent !== null;
  } catch (error) {
    return false;
  }
};

/**
 * Check if user has given analytics consent (PostHog)
 * @returns {boolean} True if analytics consent is granted
 */
export const hasAnalyticsConsent = () => {
  try {
    const consentData = getConsentData();
    if (!consentData) {
      return false;
    }
    return consentData.analytics === true;
  } catch (error) {
    return false;
  }
};

const dispatchConsentEvent = () => {
  window.dispatchEvent(new CustomEvent('cookie-consent-changed'));
};

/**
 * Accept all cookies (necessary + analytics + notifications)
 */
export const acceptAllCookies = () => {
  const consentData = {
    necessary: true,
    analytics: true,
    notifications: true,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  localStorage.setItem('cookie_consent', JSON.stringify(consentData));
  dispatchConsentEvent();
};

/**
 * Save consent preferences for analytics and notifications
 * @param {boolean} analytics - Analytics consent value
 * @param {boolean} notifications - Notifications consent value
 */
export const saveConsentPreferences = (analytics, notifications) => {
  const consentData = {
    necessary: true,
    analytics: analytics,
    notifications: notifications,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  localStorage.setItem('cookie_consent', JSON.stringify(consentData));
  dispatchConsentEvent();
};

/**
 * Delete a cookie across all relevant path/domain combinations.
 */
const nukeCookie = (cookieName) => {
  const paths = ['/', '', '/app', '/login'];
  const hostname = window.location.hostname;
  const domains = ['', hostname, `.${hostname}`];

  const parts = hostname.split('.');
  if (parts.length > 1) {
    const rootDomain = parts.slice(-2).join('.');
    domains.push(rootDomain, `.${rootDomain}`);
  }
  if (parts.length > 2) {
    domains.push(parts.slice(-3).join('.'), `.${parts.slice(-3).join('.')}`);
  }

  paths.forEach(path => {
    domains.forEach(domain => {
      const pathPart = path ? `; path=${path}` : '';
      const domainPart = domain ? `; domain=${domain}` : '';

      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC${pathPart}${domainPart}`;
      document.cookie = `${cookieName}=; max-age=0${pathPart}${domainPart}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}; SameSite=Lax`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}; SameSite=None; Secure`;
    });
  });
};

/**
 * Clear PostHog-related cookies and storage.
 * disablePostHog() is synchronous and disables persistence before destroying
 * the instance, so no new cookies can be written between disable and cleanup.
 */
export const clearPostHogData = () => {
  disablePostHog();

  const posthogKeys = getPostHogStorageKeys();
  posthogKeys.localStorage.forEach(key => localStorage.removeItem(key));
  posthogKeys.sessionStorage.forEach(key => sessionStorage.removeItem(key));

  const patterns = ['ph_', 'ph-', 'posthog', '__ph'];
  document.cookie.split(';').forEach(c => {
    const name = c.trim().split('=')[0];
    if (patterns.some(p => name.toLowerCase().includes(p.toLowerCase()))) {
      nukeCookie(name);
    }
  });
};

/**
 * Clear all analytics-related cookies and storage
 */
export const clearAnalyticsCookies = () => {
  clearPostHogData();
};

/**
 * Update specific consent category
 * @param {string} category - 'analytics' or 'notifications'
 * @param {boolean} value - Consent value
 */
export const updateConsentCategory = (category, value) => {
  const currentConsent = getConsentData() || {
    necessary: true,
    analytics: false,
    notifications: false,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  const updatedConsent = {
    ...currentConsent,
    [category]: value,
    timestamp: new Date().toISOString(),
  };

  localStorage.setItem('cookie_consent', JSON.stringify(updatedConsent));
};

/**
 * Reject optional cookies (only necessary cookies allowed)
 */
export const rejectOptionalCookies = () => {
  const consentData = {
    necessary: true,
    analytics: false,
    notifications: false,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  clearAnalyticsCookies();
  localStorage.setItem('cookie_consent', JSON.stringify(consentData));
  dispatchConsentEvent();
};

/**
 * Clear all consent data
 */
export const resetAllConsent = () => {
  clearAnalyticsCookies();
  localStorage.removeItem('cookie_consent');
};

/**
 * Initialize consent system on app load
 * Handles migration and cleanup for users without proper consent
 */
export const initializeConsent = async () => {
  const consentData = getConsentData();
  
  if (!consentData) {
    const { removeAnalyticsScripts } = await import('./scriptLoadingUtils');
    removeAnalyticsScripts();
    return;
  }

  if (!consentData.analytics) {
    clearPostHogData();
  }
};